import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Plane, BedDouble, Bus, Luggage, QrCode, ScanLine, CalendarDays, MapPin, Clock, ArrowRight,
} from 'lucide-react';
import useFetch from '../../hooks/useFetch';
import { useAuth } from '../../context/AuthContext';
import { useEvents } from '../../context/EventContext';
import EventPicker from '../../components/EventPicker';
import { Card, Badge, StatusBadge, Loader, EmptyState, stagger, fadeUp } from '../../components/UI';
import { fmtDate, fmtTime, fmtDateTime, EVENT_TYPE_LABEL } from '../../utils/format';
import { AlertCard } from './shared';

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
}

export default function AttendeeHome() {
  const { user } = useAuth();
  const { event, events, eventId, hasModule, loading: eventsLoading } = useEvents();

  const { data: alert, reload: reloadAlert } = useFetch('/home-alerts/active', { event: eventId }, { skip: !eventId });
  const { data: days } = useFetch(eventId ? `/events/${eventId}/schedule` : null, {}, { skip: !eventId });
  const { data: flights } = useFetch('/flights/my', { event: eventId }, { skip: !eventId || !hasModule('flights') });
  const { data: room } = useFetch('/rooms/my', { event: eventId }, { skip: !eventId || !hasModule('hotelBooking') });

  if (eventsLoading) return <Loader full label="Loading your event" />;
  if (!events.length) {
    return <EmptyState message="You are not registered for an event yet" hint="Your event will appear here as soon as your coordinator adds you." icon={CalendarDays} />;
  }

  const now = Date.now();
  const start = event?.startDate ? new Date(event.startDate) : null;
  const daysToGo = start ? Math.ceil((start.setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0)) / 86400000) : null;
  const next = (days || []).flatMap((d) => d.sessions).find((s) => new Date(s.endAt || s.startAt).getTime() >= now);
  const onward = flights?.onward;
  const handlesLuggage = hasModule('onboardingLuggage') || hasModule('arrivalLuggage');

  const cards = [
    { to: '/attendee/check-in', icon: ScanLine, title: 'Check in', value: 'Scan a session code', sub: 'Mark yourself present', show: true },
    { to: '/attendee/pass', icon: QrCode, title: 'Digital pass', value: 'Show your QR', sub: 'For entry and boarding', show: true },
    {
      to: '/attendee/travel', icon: Plane, title: 'Travel', show: true,
      value: hasModule('flights') ? (onward?.flightNumber || 'Not assigned') : 'Not included',
      sub: hasModule('flights') ? (onward?.departureAt ? fmtDateTime(onward.departureAt) : 'Awaiting your ticket') : 'This event does not book flights',
    },
    {
      to: '/attendee/stay', icon: BedDouble, title: 'Your stay', show: hasModule('hotelBooking'),
      value: room?.roomNumber ? `Room ${room.roomNumber}` : 'Not assigned', sub: room?.hotel?.name || 'Awaiting allocation',
    },
    { to: '/attendee/transport', icon: Bus, title: 'Transport', value: 'Coach & seat', sub: 'Boarding point and timing', show: hasModule('buses') },
    { to: '/attendee/luggage', icon: Luggage, title: 'Luggage', value: 'Your bags', sub: 'Tags and delivery', show: handlesLuggage },
  ].filter((c) => c.show);

  return (
    <motion.div variants={stagger} initial="hidden" animate="show">
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap', marginBottom: 18 }}>
        <div>
          <p style={{ fontSize: 13, color: 'var(--text-lo)' }}>{greeting()}</p>
          <h1 style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-0.02em' }}>{user?.name?.split(' ')[0]}</h1>
        </div>
        {events.length > 1 && <EventPicker />}
      </div>

      <motion.div variants={fadeUp}>
        <Card padding={24} style={{ marginBottom: 18, overflow: 'hidden', background: 'linear-gradient(135deg, #1A1A22, #121218)' }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Badge tone="gold">{EVENT_TYPE_LABEL[event?.type] || 'Event'}</Badge>
            <StatusBadge status={event?.status} />
          </div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 25, fontWeight: 600, marginTop: 12, lineHeight: 1.25 }}>{event?.title}</h2>
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 12, fontSize: 13, color: 'var(--text-mid)' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}><CalendarDays size={14} color="var(--gold-500)" />{fmtDate(event?.startDate)} – {fmtDate(event?.endDate)}</span>
            {(event?.venue?.name || event?.venue?.city) && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}><MapPin size={14} color="var(--gold-500)" />{[event.venue.name, event.venue.city].filter(Boolean).join(', ')}</span>
            )}
          </div>
          {daysToGo !== null && daysToGo >= 0 && (
            <p style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--ink-750)', fontSize: 13, color: 'var(--text-mid)' }}>
              <span style={{ fontSize: 28, fontWeight: 700, color: 'var(--gold-400)', marginRight: 8 }}>{daysToGo}</span>
              {daysToGo === 0 ? 'Today is the day' : `day${daysToGo === 1 ? '' : 's'} to go`}
            </p>
          )}
        </Card>
      </motion.div>

      {alert && (
        <motion.div variants={fadeUp} style={{ marginBottom: 18 }}>
          <AlertCard key={`${alert._id}-${alert.submitted}-${alert.answered}`} alert={alert} eventId={eventId} onChanged={reloadAlert} />
        </motion.div>
      )}

      {next && (
        <motion.div variants={fadeUp} style={{ marginBottom: 18 }}>
          <Link to="/attendee/schedule">
            <Card hover padding={18}>
              <p style={{ fontSize: 11.5, color: 'var(--gold-500)', textTransform: 'uppercase', letterSpacing: 0.9, fontWeight: 600 }}>Up next</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
                <Clock size={16} color="var(--text-lo)" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 15, fontWeight: 600 }}>{next.title}</p>
                  <p style={{ fontSize: 12.5, color: 'var(--text-mid)', marginTop: 2 }}>{fmtDate(next.startAt)} · {fmtTime(next.startAt)}</p>
                </div>
                <ArrowRight size={15} color="var(--text-lo)" />
              </div>
            </Card>
          </Link>
        </motion.div>
      )}

      <p style={{ fontSize: 11.5, color: 'var(--text-lo)', textTransform: 'uppercase', letterSpacing: 0.9, fontWeight: 600, marginBottom: 10 }}>Your event</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 13 }}>
        {cards.map((c) => (
          <motion.div key={c.to} variants={fadeUp} style={{ display: 'flex' }}>
            <Link to={c.to} style={{ display: 'flex', flex: 1 }}>
              <Card hover padding={17} style={{ flex: 1 }}>
                <div style={{ width: 34, height: 34, borderRadius: 'var(--r-sm)', background: 'rgba(239,212,138,0.12)', display: 'grid', placeItems: 'center', marginBottom: 12 }}>
                  <c.icon size={17} color="var(--gold-400)" />
                </div>
                <p style={{ fontSize: 11, color: 'var(--text-lo)', textTransform: 'uppercase', letterSpacing: 0.7, fontWeight: 600 }}>{c.title}</p>
                <p style={{ fontSize: 15, fontWeight: 600, marginTop: 5 }}>{c.value}</p>
                <p style={{ fontSize: 12, color: 'var(--text-lo)', marginTop: 3 }}>{c.sub}</p>
              </Card>
            </Link>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
}
